import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSceneScreenshotStatus } from "../../redux/asyncThunk/userActions";
import { inferScenarioContentMode } from "../../utils/contentMode";
import { getServerMediaUrl } from "../../utils/mediaUrl";

export { getServerMediaUrl } from "../../utils/mediaUrl";

const SCREENSHOT_POLL_INTERVAL_MS = 2000;
const SCREENSHOT_POLL_TIMEOUT_MS = 3 * 60 * 1000;

export function useSceneScreenshot(nodeId, propsData) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user?.game);
  const userId = useSelector((state) => state.user?.data?.user?.id);
  const selectedGameId = useSelector((state) => state.user?.selectedGameId);
  const [imageKey, setImageKey] = useState(0);

  const sceneData =
    game?.nodes?.find((node) => String(node.id) === String(nodeId))?.data ??
    propsData;
  const contentMode = inferScenarioContentMode(sceneData);
  const screenpath = sceneData?.screenpath;
  const imagepath = sceneData?.imagepath;
  const hasScreenshot = contentMode === "video" && Boolean(screenpath);
  const hasVideo =
    contentMode === "video" && Boolean(sceneData?.hasVideo || sceneData?.video === "X");
  const screenshotStatus = sceneData?.screenshotStatus;
  const isScreenshotPending =
    hasVideo && !screenpath && screenshotStatus === "pending";
  const hasImagePreview = contentMode === "image" && Boolean(imagepath);
  const imageSrc = hasScreenshot
    ? getServerMediaUrl(screenpath, imageKey)
    : null;
  const imagePreviewSrc = hasImagePreview
    ? getServerMediaUrl(imagepath, imageKey)
    : null;

  useEffect(() => {
    if (screenpath || imagepath) {
      setImageKey((prev) => prev + 1);
    }
  }, [screenpath, imagepath]);

  // The screenshot is made by the server after the upload response: ask for it
  // until it is ready and the slice puts it into the scene data.
  useEffect(() => {
    if (!isScreenshotPending || !userId || !selectedGameId) return undefined;

    let cancelled = false;
    let timer = null;
    const deadline = Date.now() + SCREENSHOT_POLL_TIMEOUT_MS;

    const poll = async () => {
      if (cancelled || Date.now() > deadline) return;
      try {
        await dispatch(
          fetchSceneScreenshotStatus({
            userId,
            selectedGameId,
            scene: String(nodeId),
          }),
        ).unwrap();
      } catch {
        // The next attempt will retry; the placeholder stays meanwhile.
      }
      if (!cancelled) {
        timer = window.setTimeout(poll, SCREENSHOT_POLL_INTERVAL_MS);
      }
    };
    timer = window.setTimeout(poll, SCREENSHOT_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isScreenshotPending, userId, selectedGameId, nodeId, dispatch]);

  const refreshScreenshot = useCallback(() => {
    setImageKey((prev) => prev + 1);
  }, []);

  return {
    sceneData,
    contentMode,
    hasScreenshot,
    hasVideo,
    screenshotStatus,
    hasImagePreview,
    imageSrc,
    imagePreviewSrc,
    imageKey,
    refreshScreenshot,
  };
}
